import React from 'react';
import { AlertTriangle, Activity, Cloud, Settings } from 'lucide-react';
import Button from '../../../components/ui/Button';

const TriggerLibrary = ({ triggerType, onAddNode }) => {
  const triggerOptions = [
    {
      type: 'ai_compliance_prediction',
      icon: AlertTriangle,
      label: 'AI Compliance Prediction',
      description: 'Trigger when OpenAI predicts compliance violation',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      conditions: [
        { key: 'confidenceThreshold', label: 'Confidence Threshold', type: 'number', default: 0.7, min: 0, max: 1, step: 0.1 },
        { key: 'violationType', label: 'Violation Type', type: 'select', options: [
          { value: 'organic_certification', label: 'Organic Certification' },
          { value: 'pesticide_limit', label: 'Pesticide Limit' },
          { value: 'application_timing', label: 'Application Timing' },
          { value: 'record_keeping', label: 'Record Keeping' }
        ]}
      ]
    },
    {
      type: 'pest_pressure_spike',
      icon: Activity,
      label: 'Pest Pressure Spike',
      description: 'Trigger on pest outbreak detection',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      conditions: [
        { key: 'severityLevel', label: 'Severity Level', type: 'select', options: [
          { value: 'low', label: 'Low' },
          { value: 'medium', label: 'Medium' },
          { value: 'high', label: 'High' },
          { value: 'critical', label: 'Critical' }
        ]},
        { key: 'pestType', label: 'Pest Type', type: 'text', placeholder: 'e.g., aphids, mites' }
      ]
    },
    {
      type: 'weather_alert',
      icon: Cloud,
      label: 'Weather Alert',
      description: 'Trigger on adverse weather conditions',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      conditions: [
        { key: 'alertType', label: 'Alert Type', type: 'select', options: [
          { value: 'frost', label: 'Frost Warning' },
          { value: 'heat', label: 'Heat Warning' },
          { value: 'rain', label: 'Heavy Rain' },
          { value: 'wind', label: 'High Wind' }
        ]}
      ]
    },
    {
      type: 'custom_condition',
      icon: Settings,
      label: 'Custom Condition',
      description: 'Define custom trigger conditions',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      conditions: [
        { key: 'conditionExpression', label: 'Condition Expression', type: 'text', placeholder: 'e.g., temperature > 85' }
      ]
    }
  ];

  const handleAddTrigger = (trigger) => {
    const defaultConditions = {};
    trigger?.conditions?.forEach(cond => {
      if (cond?.default !== undefined) {
        defaultConditions[cond?.key] = cond?.default;
      } else if (cond?.options?.length > 0) {
        defaultConditions[cond?.key] = cond?.options?.[0]?.value;
      }
    });

    onAddNode({
      type: 'trigger',
      label: trigger?.label,
      config: {
        triggerType: trigger?.type,
        conditions: defaultConditions
      },
      icon: trigger?.icon,
      color: trigger?.color,
      bgColor: trigger?.bgColor
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Trigger Library</h2>
      <p className="text-sm text-gray-600 mb-4">
        Select a trigger to start your workflow
      </p>

      <div className="space-y-3">
        {triggerOptions?.map(trigger => {
          const IconComponent = trigger?.icon;
          return (
            <div
              key={trigger?.type}
              className={`p-3 rounded-lg border-2 transition-all ${
                triggerType === trigger?.type
                  ? 'border-green-500 bg-green-50' :'border-gray-200 hover:border-green-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${trigger?.bgColor}`}>
                  <IconComponent className={`w-5 h-5 ${trigger?.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 text-sm">{trigger?.label}</h3>
                  <p className="text-xs text-gray-500 mt-1">{trigger?.description}</p>
                  <Button
                    onClick={() => handleAddTrigger(trigger)}
                    size="sm"
                    variant="outline"
                    className="mt-2 w-full"
                  >
                    Add Trigger
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          <strong>Tip:</strong> Workflows must have at least one trigger node to execute. Configure confidence thresholds to control when workflows activate.
        </p>
      </div>
    </div>
  );
};

export default TriggerLibrary;